import React from 'react';
import { Client } from '@stomp/stompjs';
import { debounce } from './debounce';

export default function useWebsocket({ url, skip = false }) {
  const clientRef = React.useRef(null);
  const [isConnected, setIsConnected] = React.useState(false);
  const [websocketData, setWebsocketData] = React.useState({});
  const [canProcess, setCanProcess] = React.useState(false);

  const subscribe = React.useCallback((paths = []) => {
    if (isConnected) {
      if (paths.length > 0) {
        paths.forEach((path) => {
          clientRef.current.subscribe(path, (msg) => {
            setCanProcess(false);
            setWebsocketData((cur) => {
              const tmp = JSON.parse(JSON.stringify(cur));
              if (tmp[path]) {
                tmp[path] = [...tmp[path], JSON.parse(msg.body)];
              } else {
                tmp[path] = [JSON.parse(msg.body)];
              }
              return tmp;
            });
          });
        });
      }
    }
  }, [isConnected]);

  React.useEffect(() => {
    debounce(() => {
      setCanProcess(true);
    }, 200)();
  }, [websocketData]);

  const removeData = React.useCallback((path, index) => {
    setWebsocketData((cur) => {
      const tmp = JSON.parse(JSON.stringify(cur));
      const values = tmp[path];
      values.splice(index, 1);
      if (values.length === 0) {
        delete tmp[path];
      }
      return tmp;
    });
  }, []);

  const processMessage = React.useCallback((path, callback, onCompleteCallback) => {
    if (websocketData[path] && canProcess) {
      websocketData[path].forEach((data, index) => {
        callback(data, index);
        removeData(path, index);
      });
      onCompleteCallback();
    }
  }, [canProcess, removeData, websocketData]);

  const disconnect = React.useCallback(() => {
    if (isConnected) {
      clientRef.current.forceDisconnect();
      clientRef.current.deactivate();
    }
  }, [isConnected]);

  React.useEffect(() => {
    if (!isConnected && !skip) {
      const client = new Client();
      client.brokerURL = url;
      client.onConnect = () => {
        setIsConnected(true);
      };
      client.onDisconnect = () => {
        console.log('%c ws disconnected', 'color: crimson');
      };
      client.onWebSocketClose = () => {
        console.log('%c ws closed', 'color: darkgrey');
      };
      client.activate();
      clientRef.current = client;
    }
  }, [isConnected, skip, url]);

  return {
    isConnected,
    disconnect,
    websocketData,
    subscribe,
    removeData,
    processMessage,
  };
}
